import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@carrent/core'
import { PageHeader, StatusBadge, Icon, DueBadge } from '@carrent/ui'
import {
  useTasks,
  availableScopes,
  canCreateTask,
  statusMeta,
  priorityMeta,
  describeDue,
  matchesDueFilter,
  dueBucket,
  TASK_DUE_FILTERS,
  TASK_STATUS_FILTERS,
} from '@carrent/domain'

/**
 * Задачи водителя — лента карточек.
 *
 * Мобильная композиция: одно основное действие на карточку (открыть),
 * крупные зоны нажатия, фильтры чипами. Плотная таблица с сортировкой
 * и массовыми действиями живёт в консоли.
 */
function TaskCard({ task, onOpen }) {
  const st = statusMeta(task.status)
  const pr = priorityMeta(task.priority)
  // Срок — то, из-за чего водитель открывает список. Раньше карточка
  // показывала только флаг «Просрочено», без ответа на вопрос «когда».
  const due = describeDue(task)

  return (
    <button
      type="button"
      className={`task-card ${due?.isOverdue ? 'task-card--overdue' : ''}`.trim()}
      onClick={onOpen}
    >
      <span className={`task-card__stripe task-card__stripe--${st.tone}`} aria-hidden="true" />

      <span className="task-card__body">
        <span className="task-card__head">
          <span className="task-card__id">#{task.id}</span>
          <span className="task-card__title">{task.title}</span>
        </span>

        <span className="task-card__tags">
          <StatusBadge status={task.status} />
          {task.priority !== 'normal' && (
            <span className={`badge badge--${pr.tone}`}>{pr.label}</span>
          )}
          <DueBadge due={due} compact />
        </span>

        {task.car && (
          <span className="task-card__car">
            <Icon name="car" size={13} />
            {task.car.brand} {task.car.model} · {task.car.plate}
          </span>
        )}
      </span>

      <Icon name="chevronRight" size={18} className="task-card__chevron" />
    </button>
  )
}

export default function TasksPage() {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [status, setStatus] = useState('')
  const [scope, setScope] = useState('all')
  const [due, setDue] = useState('')
  const { tasks, loading, error, reload } = useTasks({ status, scope })

  const scopes = availableScopes(user)

  /**
   * Мобильная композиция: не таблица со столбцом срока, а разделы
   * по срочности. Первым идёт то, что горит, — на телефоне человек
   * читает сверху вниз и до конца списка чаще всего не доходит.
   */
  const sections = useMemo(() => {
    const now = new Date()
    const visible = tasks.filter((t) => matchesDueFilter(t, due, now))
    const buckets = {
      overdue: { title: 'Просрочено', items: [] },
      today: { title: 'Сегодня', items: [] },
      tomorrow: { title: 'Завтра', items: [] },
      week: { title: 'На этой неделе', items: [] },
      later: { title: 'Позже', items: [] },
      none: { title: 'Без срока', items: [] },
      done: { title: 'Закрытые', items: [] },
    }
    visible.forEach((t) => {
      buckets[dueBucket(t, now)].items.push(t)
    })
    Object.values(buckets).forEach((b) =>
      b.items.sort((a, z) => {
        const av = a.due_at ? new Date(a.due_at).getTime() : Infinity
        const zv = z.due_at ? new Date(z.due_at).getTime() : Infinity
        return av - zv
      })
    )
    return Object.entries(buckets)
      .filter(([, b]) => b.items.length > 0)
      .map(([key, b]) => ({ key, ...b }))
  }, [tasks, due])

  const visibleCount = sections.reduce((sum, s) => sum + s.items.length, 0)

  return (
    <div>
      <PageHeader
        title="Задачи"
        right={
          canCreateTask(user) ? (
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => navigate('/tasks/new')}
            >
              <Icon name="plus" size={14} />
              Создать
            </button>
          ) : undefined
        }
      />

      <div className="task-filters">
        {scopes.length > 1 && (
          <div className="chip-row" role="tablist" aria-label="Чьи задачи">
            {scopes.map((s) => (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={scope === s.key}
                className={`chip ${scope === s.key ? 'chip--active' : ''}`}
                onClick={() => setScope(s.key)}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        <div className="chip-row" role="tablist" aria-label="Срок">
          {TASK_DUE_FILTERS.map((f) => (
            <button
              key={f.key || 'any'}
              type="button"
              role="tab"
              aria-selected={due === f.key}
              className={`chip ${due === f.key ? 'chip--active' : ''}`}
              onClick={() => setDue(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="chip-row" role="tablist" aria-label="Статус">
          {TASK_STATUS_FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              type="button"
              role="tab"
              aria-selected={status === f.key}
              className={`chip ${status === f.key ? 'chip--active' : ''}`}
              onClick={() => setStatus(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-page flex-col gap-sm">
        {loading && (
          <>
            <span className="skeleton task-card__skeleton" />
            <span className="skeleton task-card__skeleton" />
            <span className="skeleton task-card__skeleton" />
          </>
        )}

        {!loading && error && (
          <div className="state">
            <p className="state__title">{error}</p>
            <button type="button" className="btn btn--secondary" onClick={reload}>
              Повторить
            </button>
          </div>
        )}

        {!loading && !error && visibleCount === 0 && (
          <div className="state">
            <Icon name="tasks" size={32} />
            <p className="state__title">Задач нет</p>
            <p className="state__text">
              {status || due || scope !== 'all'
                ? 'Попробуйте изменить фильтры.'
                : 'Новые задачи появятся здесь.'}
            </p>
          </div>
        )}

        {!loading &&
          !error &&
          sections.map((section) => (
            <section key={section.key} className="task-section">
              <h2 className={`task-section__title task-section__title--${section.key}`}>
                {section.title}
                <span className="task-section__count">{section.items.length}</span>
              </h2>
              {section.items.map((t) => (
                <TaskCard key={t.id} task={t} onOpen={() => navigate(`/tasks/${t.id}`)} />
              ))}
            </section>
          ))}
      </div>
    </div>
  )
}
